import { LocalePrefix } from './LocalePrefix';

const REDIS_CONNECT_MAX_RETRIES_DEFAULT = 3;

export type EnvConfig = {
    apiBaseUrl: string;
    apiKey: string;
    baseHref: string;
    clientApiKey: string;
    estoreConsumerVersion?: string;

    cookieDomain: string;
    defaultCachePolicy?: RequestCache;
    i18nLocaleDetectionEnabled: boolean;
    i18nLocalePrefix: LocalePrefix;
    runtimeEnvironment: string;
    buildEnvironment: string;

    // Tracking environment variables
    trackingBaseUrl: string;
    trackingABTestPath: string;
    FRTrackingAnalyticsPath: string;
    FRTrackingMediaPath: string;
    PLTrackingAnalyticsPath: string;
    PLTrackingMediaPath: string;
    ITTrackingAnalyticsPath: string;
    ITTrackingMediaPath: string;
    ESTrackingAnalyticsPath: string;
    ESTrackingMediaPath: string;
    DETrackingAnalyticsPath: string;
    DETrackingMediaPath: string;
    CZTrackingAnalyticsPath: string;
    CZTrackingMediaPath: string;
    ROTrackingAnalyticsPath: string;
    ROTrackingMediaPath: string;
    DKTrackingAnalyticsPath: string;
    DKTrackingMediaPath: string;
    SETrackingAnalyticsPath: string;
    SETrackingMediaPath: string;
    PTTrackingAnalyticsPath: string;
    PTTrackingMediaPath: string;
    TRTrackingAnalyticsPath: string;
    TRTrackingMediaPath: string;
    GRTrackingAnalyticsPath: string;
    GRTrackingMediaPath: string;
    CHTrackingAnalyticsPath: string;
    CHTrackingMediaPath: string;
    HRTrackingAnalyticsPath: string;
    HRTrackingMediaPath: string;
    woosmapApiBaseUrl: string;
    fetchTimeoutMs: number;
    storageBasePath: string;
    timeMachineEnabled: boolean;
    opentelemetryEnabled: boolean;
    appVersion?: string;
    serverEncryptionIV: string;
    serverEncryptionSalt: string;
    serverEncryptionSecret: string;
    ekooWebsiteId: string;
    sanityCheckServerSecretKey?: string;
    earlyHintsServerSecretKey?: string;
    mySephoraInstanceId?: string;
    mySephoraHideLoggedGreetingMention: boolean;
    redisPassword?: string;
    redisUrl?: string;
    redisCacheRevalidationSecretKey?: string;
    redisConnectMaxRetries: number;
    cacheDefaultTtlSeconds?: number;
    cacheDefaultStaleTimeSeconds?: number;
    cacheBasketIdTtlSeconds?: number;
    cacheBasketIdStaleTimeSeconds?: number;
};

// https://nextjs.org/docs/app/building-your-application/data-fetching/fetching#reusing-data-across-multiple-functions
// In previous versions of Next.js, using fetch would have a default cache value of force-cache.
// This changed in version 15, to a default of cache: no-store.
// No-need to set this value in the config, as Next default value suits our needs.
const DEFAULT_CACHE_POLICY: RequestCache | undefined = undefined;
const DEFAULT_RUNTIME_ENVIRONMENT = 'prod';
const DEFAULT_BUILD_ENVIRONMENT = 'prd';
const DEFAULT_FETCH_TIMEOUT_MS = 30000;
const DEFAULT_BASE_HREF = '';

const parseEnvNumber = (value: string | undefined): number | undefined => {
    if (!value) {
        return undefined;
    }
    const parsedValue = Number(value);

    if (isNaN(parsedValue)) {
        return undefined;
    }

    return parsedValue;
};

export const createEnvConfig = (): EnvConfig => {
    return {
        apiBaseUrl: process.env['API_BASE_URL'],
        apiKey: process.env['API_KEY'],
        baseHref: process.env['BASE_HREF'] ?? DEFAULT_BASE_HREF,
        clientApiKey: process.env['CLIENT_API_KEY'],
        estoreConsumerVersion: process.env['ESTORE_CONSUMER_VERSION'],

        cookieDomain: process.env['COOKIE_DOMAIN'],
        defaultCachePolicy: process.env['DEFAULT_CACHE_POLICY'] ?? DEFAULT_CACHE_POLICY,
        i18nLocaleDetectionEnabled: process.env['I18N_LOCALE_DETECTION_ENABLED'] === 'true',
        i18nLocalePrefix: (process.env['I18N_LOCALE_PREFIX'] as LocalePrefix) ?? LocalePrefix.Always,
        runtimeEnvironment: process.env['RUNTIME_ENV'] ?? DEFAULT_RUNTIME_ENVIRONMENT,
        buildEnvironment: process.env['ENV_BUILD'] ?? DEFAULT_BUILD_ENVIRONMENT,
        trackingBaseUrl: process.env['TRACKING_BASE_URL'],
        trackingABTestPath: process.env['TRACKING_AB_TEST_PATH'],
        FRTrackingAnalyticsPath: process.env['FR_TRACKING_ANALYTICS_PATH'],
        FRTrackingMediaPath: process.env['FR_TRACKING_MEDIA_PATH'],
        PLTrackingAnalyticsPath: process.env['PL_TRACKING_ANALYTICS_PATH'],
        PLTrackingMediaPath: process.env['PL_TRACKING_MEDIA_PATH'],
        ITTrackingAnalyticsPath: process.env['IT_TRACKING_ANALYTICS_PATH'],
        ITTrackingMediaPath: process.env['IT_TRACKING_MEDIA_PATH'],
        ESTrackingAnalyticsPath: process.env['ES_TRACKING_ANALYTICS_PATH'],
        ESTrackingMediaPath: process.env['ES_TRACKING_MEDIA_PATH'],
        DETrackingAnalyticsPath: process.env['DE_TRACKING_ANALYTICS_PATH'],
        DETrackingMediaPath: process.env['DE_TRACKING_MEDIA_PATH'],
        CZTrackingAnalyticsPath: process.env['CZ_TRACKING_ANALYTICS_PATH'],
        CZTrackingMediaPath: process.env['CZ_TRACKING_MEDIA_PATH'],
        ROTrackingAnalyticsPath: process.env['RO_TRACKING_ANALYTICS_PATH'],
        ROTrackingMediaPath: process.env['RO_TRACKING_MEDIA_PATH'],
        DKTrackingAnalyticsPath: process.env['DK_TRACKING_ANALYTICS_PATH'],
        DKTrackingMediaPath: process.env['DK_TRACKING_MEDIA_PATH'],
        SETrackingAnalyticsPath: process.env['SE_TRACKING_ANALYTICS_PATH'],
        SETrackingMediaPath: process.env['SE_TRACKING_MEDIA_PATH'],
        PTTrackingAnalyticsPath: process.env['PT_TRACKING_ANALYTICS_PATH'],
        PTTrackingMediaPath: process.env['PT_TRACKING_MEDIA_PATH'],
        TRTrackingAnalyticsPath: process.env['TR_TRACKING_ANALYTICS_PATH'],
        TRTrackingMediaPath: process.env['TR_TRACKING_MEDIA_PATH'],
        GRTrackingAnalyticsPath: process.env['GR_TRACKING_ANALYTICS_PATH'],
        GRTrackingMediaPath: process.env['GR_TRACKING_MEDIA_PATH'],
        CHTrackingAnalyticsPath: process.env['CH_TRACKING_ANALYTICS_PATH'],
        CHTrackingMediaPath: process.env['CH_TRACKING_MEDIA_PATH'],
        HRTrackingAnalyticsPath: process.env['HR_TRACKING_ANALYTICS_PATH'],
        HRTrackingMediaPath: process.env['HR_TRACKING_MEDIA_PATH'],
        woosmapApiBaseUrl: process.env['WOOSMAP_API_BASE_URL'],
        fetchTimeoutMs: Number(process.env['FETCH_TIMEOUT_MS'] ?? DEFAULT_FETCH_TIMEOUT_MS),
        storageBasePath: process.env['STORAGE_BASE_PATH'],
        timeMachineEnabled: process.env['TIME_MACHINE_ENABLED'] === 'true',
        opentelemetryEnabled: process.env['OPENTELEMETRY_ENABLED'] === 'true',
        appVersion: process.env['NEXT_PUBLIC_APP_VERSION'],
        serverEncryptionIV: process.env['SERVER_ENCRYPTION_IV'],
        serverEncryptionSalt: process.env['SERVER_ENCRYPTION_SALT'],
        serverEncryptionSecret: process.env['SERVER_ENCRYPTION_SECRET'],
        ekooWebsiteId: process.env['EKOO_WEBSITE_ID'],
        sanityCheckServerSecretKey: process.env['SERVER_SANITY_CHECK_SECRET_KEY'],
        earlyHintsServerSecretKey: process.env['SERVER_EARLY_HINTS_SECRET_KEY'],
        mySephoraInstanceId: process.env['MY_SEPHORA_INSTANCE_ID'],
        mySephoraHideLoggedGreetingMention: process.env['MYSEPHORA_HIDE_LOGGED_GREETING_MENTION'] === 'true',
        // Redis env variables
        redisPassword: process.env['REDIS_PASSWORD'],
        redisUrl: process.env['REDIS_URL'],
        redisCacheRevalidationSecretKey: process.env['REDIS_CACHE_REVALIDATION_SECRET_KEY'],
        // Cache env variables,
        cacheDefaultTtlSeconds: parseEnvNumber(process.env['CACHE_DEFAULT_TTL_SECONDS']),
        cacheDefaultStaleTimeSeconds: parseEnvNumber(process.env['CACHE_DEFAULT_STALE_TIME_SECONDS']),
        cacheBasketIdTtlSeconds: parseEnvNumber(process.env['CACHE_BASKET_ID_TTL_SECONDS']),
        cacheBasketIdStaleTimeSeconds: parseEnvNumber(process.env['CACHE_BASKET_ID_STALE_TIME_SECONDS']),
        redisConnectMaxRetries:
            parseEnvNumber(process.env['REDIS_CONNECT_MAX_RETRIES']) ?? REDIS_CONNECT_MAX_RETRIES_DEFAULT,
    };
};

export const envConfig = createEnvConfig();

export const isServer = (): boolean => !!process.env['NEXT_RUNTIME'];
export const isLocalEnv = (): boolean => envConfig.runtimeEnvironment === 'local';
